import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useLocation, useParams } from 'react-router';

import { useGetUserLanguage } from '@/components/hooks/use-local-storage';
import { AVAILABLE_LANGUAGES_MAP_LONG_TO_SHORT } from '@/components/language/language-provider';
import { PageLoader } from '@/components/loading/page-loader';
import i18n from '@/i18n';
import {
  isLang,
  isPathLang,
  type Lang,
  localizePath,
  pickedLang,
  RTL_LANGS,
  stripLangPrefix,
} from '@/seo/marketing-routes';

const applyLang = (lang: Lang) => {
  if (i18n.language.split('-')[0] !== lang) {
    i18n.changeLanguage(lang);
  }
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lang;
    document.documentElement.dir = RTL_LANGS.includes(lang) ? 'rtl' : 'ltr';
  }
};

/**
 * Layout boundary for the public, indexable marketing routes. The URL is the
 * source of truth for language here (LanguageChanger is suppressed on marketing
 * paths — see language-provider). It:
 *
 *  - validates the `/:lang` prefix (unknown prefix → graceful redirect to the
 *    English/root version of the same path),
 *  - sets `i18n` language + `<html lang>`/`dir` (RTL for Hebrew) synchronously,
 *  - on the English root, redirects a returning user with a non-English stored
 *    preference to their localized URL (crawlers have no preference → they stay
 *    on the canonical English root) — a language picked in a menu in this tab
 *    (`pickedLang`) outranking the stored one.
 *
 * Pass `lang="en"` for the un-prefixed (root) group; omit it for the `/:lang`
 * group, where the language comes from the route param.
 */
export const LangBoundary = ({ lang: fixedLang }: { lang?: Lang }) => {
  // Subscribes this boundary to language changes (declared before the effect
  // below, so it is listening when that effect switches the language).
  const { i18n: i18nState } = useTranslation();
  const params = useParams();
  const { pathname, search } = useLocation();
  const { language: storedLanguageName } = useGetUserLanguage();

  const urlLang = fixedLang ?? params.lang;
  const invalidPrefix = !fixedLang && (!urlLang || !isPathLang(urlLang));

  const storedLang = AVAILABLE_LANGUAGES_MAP_LONG_TO_SHORT[
    storedLanguageName as keyof typeof AVAILABLE_LANGUAGES_MAP_LONG_TO_SHORT
  ] as Lang | undefined;
  // A language picked in a menu this session outranks the stored one —
  // English included, or a signed-in account's language would bounce the pick.
  const rootLang = pickedLang() ?? storedLang;
  const shouldLocalizeRoot = fixedLang === 'en' && !!rootLang && rootLang !== 'en';

  const lang: Lang = invalidPrefix ? 'en' : ((urlLang as Lang) ?? 'en');

  const current = (i18nState.language || 'en').split('-')[0];
  // A switch to a language the URL cannot carry (an AI-only one picked in
  // Settings, e.g. 'pt') is not a navigation — the URL stays the source of
  // truth, so the page is put back in the URL's language. A switch to a URL
  // language is left alone: the language dropdown navigates to its prefix.
  const currentIsUrlLang = isLang(current);

  // Once the page has spoken the URL's language it never blanks again: a later
  // language change keeps the page on screen while it re-renders.
  const [settledLang, setSettledLang] = useState<Lang | null>(null);
  if (current === lang && settledLang !== lang) setSettledLang(lang);

  useEffect(() => {
    if (invalidPrefix || shouldLocalizeRoot) return; // a redirect handles these
    // On mount / URL change always apply; once settled, only undo a switch to
    // a language the URL cannot carry.
    if (current !== lang && settledLang === lang && currentIsUrlLang) return;
    applyLang(lang);
  }, [lang, current, currentIsUrlLang, settledLang, invalidPrefix, shouldLocalizeRoot]);

  if (invalidPrefix) {
    const rest = '/' + pathname.split('/').filter(Boolean).slice(1).join('/');
    return <Navigate to={(rest === '/' ? '/' : rest) + search} replace />;
  }

  if (shouldLocalizeRoot) {
    return (
      <Navigate to={localizePath(stripLangPrefix(pathname), rootLang) + search} replace />
    );
  }

  // The page first renders only once i18n speaks the URL's language. Rendered
  // before the switch, components that missed its event stayed in the stored
  // language — /it/home came out half English and /it/home/about all English,
  // which is also what Googlebot rendered under the Italian hreflang.
  // Until then the page loader holds the screen (a language chunk is a
  // network fetch).
  if (current !== lang && settledLang !== lang) return <PageLoader variant="screen" />;

  return <Outlet />;
};
